import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import {
    Box,
    Button,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    IconButton,
    TextField,
    Checkbox,
    Stack
} from '@mui/material';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { DataGridPro, GridColDef, GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-pro';

import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import VendorSelector from './VendorSelector';

import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

import { socket } from '../services/socket';
import dayjs from 'dayjs';
import { Dayjs } from 'dayjs';

const api = axios.create({
    withCredentials: true
});

interface OutletContextType {
    isConnected: boolean;
    maestroPermissions: string[];
}

interface Cycle {
    id: number;
    startDate: string | null;
    endDate: string | null;
    vendorIds: string[];
    carryOverInvoices: boolean;
}

interface ExistingCycleApiResponse {
    cycle: {
        cycleId: number;
        startDate: string;
        endDate: string;
        carryoverInvoices: boolean;
        closedBy: string | null;
        closedDate: string | null;
    };
    vendors: {
        vendorId: number;
        companyName: string;
    }[];
}

interface VendorOption {
    id: string;
    name: string;
}

interface EditedCycleMap {
        [key: string]: Cycle;
    }

function CycleManagement(): JSX.Element {
    const { isConnected, maestroPermissions } =
        useOutletContext() as OutletContextType;

    const { getAccessTokenSilently } = useAuth0();

    const [editedExistingCycles, setEditedExistingCycles] = useState<EditedCycleMap>({});
    const [originalExistingCycles, setOriginalExistingCycles] = useState<Cycle[]>([]);
    const [existingCycles, setExistingCycles] = useState<Cycle[]>([])
    const [newCycles, setNewCycles] = useState<Cycle[]>([]);
    const [eligibleVendors, setEligibleVendors] = useState<VendorOption[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const isAuthorized = maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:Cycles:Update`) &&
        maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:Cycles:Read`)

    const handleCyclesUpdate = useRef(() => {
    });

    const handleSaveCycleUpdates = async (id: number) => {
        const cycle = editedExistingCycles[String(id)];

        if (!cycle) {
            return;
        }

        await updateExistingCycle(cycle);
    };

    const getCycleStartDate = (date: string): string => {
        const dateOnly = date.split('T')[0];
        return `${dateOnly}T00:00:01-07:00`;
    };

    const getCycleEndDate = (date: string): string => {
        const dateOnly = date.split('T')[0];
        return `${dateOnly}T23:59:59-07:00`;
    };

    const handleClearCycleUpdates = (id: number) => {
        const original = originalExistingCycles.find(r => r.id === id);
        if (!original) return;

        setExistingCycles(prev =>
            prev.map(r => (r.id === id ? structuredClone(original) : r))
        );

        setEditedExistingCycles(prev => {
            const copy = { ...prev };
            delete copy[String(id)];
            return copy;
        });
    };

    const handleDeleteCycle = async (id: number) => {
        await deleteCycle(id);
    };

    const isCycleDirty = (id: number): boolean => {
        return !!editedExistingCycles[String(id)];
    };

    const handleProcessCycleRowUpdate = (newRow: Cycle): Cycle => {
        const original = originalExistingCycles.find(r => r.id === newRow.id);

        const isDirty =
            original?.startDate !== newRow.startDate ||
            original?.endDate !== newRow.endDate ||
            JSON.stringify(original?.vendorIds ?? []) !== JSON.stringify(newRow.vendorIds ?? []) ||
            original?.carryOverInvoices !== newRow.carryOverInvoices;

        setExistingCycles(prev =>
            prev.map(r => (r.id === newRow.id ? newRow : r))
        );

        setEditedExistingCycles(prev => {
            const copy = { ...prev };

            if (isDirty) {
                copy[String(newRow.id)] = newRow;
            } else {
                delete copy[String(newRow.id)];
            }

            return copy;
        });

        return newRow;
    };

    async function deleteCycle(cycleId: number): Promise<void> {
        try {
            setIsLoading(true);

            const audience =
                process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            await api.delete(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles/${cycleId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            setExistingCycles(prev =>
                prev.filter(c => c.id !== cycleId)
            );

            setOriginalExistingCycles(prev =>
                prev.filter(c => c.id !== cycleId)
            );

            setEditedExistingCycles(prev => {
                const copy = { ...prev };
                delete copy[String(cycleId)];
                return copy;
            });

            toast.success('Cycle deleted successfully');
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                'Failed to delete cycle'
            );
        } finally {
            getVendorAndCycleData();
            setIsLoading(false);
        }
    }

    async function getVendorAndCycleData(): Promise<void> {
        try {
            setIsLoading(true);

            const audience =
                process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            const eligibleVendorsResponse = await api.get(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/revProVendors/commissions/eligible`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            const existingCyclesResponse = await api.get(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            const mappedCycles: Cycle[] = (
                existingCyclesResponse.data as ExistingCycleApiResponse[]
            )
                .filter(item => {
                    const cycle = item.cycle;

                    return !cycle.closedBy && !cycle.closedDate;
                })
                .map(item => ({
                    id: item.cycle.cycleId,
                    startDate: item.cycle.startDate,
                    endDate: item.cycle.endDate,
                    carryOverInvoices: item.cycle.carryoverInvoices,
                    vendorIds: item.vendors.map(v => String(v.vendorId))
                }));

            const mappedVendors: VendorOption[] = eligibleVendorsResponse.data.map((v: any) => ({
                id: String(v.vendorId),
                name: v.companyName
            }));

            setExistingCycles(mappedCycles);
            setOriginalExistingCycles(structuredClone(mappedCycles));
            setEditedExistingCycles({});
            setEligibleVendors(mappedVendors);
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                    'Failed to load vendors'
            );
        } finally {
            setIsLoading(false);
        }
    }

    async function updateExistingCycle(cycle: Cycle): Promise<void> {
        try {
            setIsLoading(true);

            const audience =
                process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            if (!cycle.startDate || !cycle.endDate) {
                toast.error('Start and End dates are required');
                return;
            }

            const updatePayload = {
                startDate: getCycleStartDate(cycle.startDate),
                endDate: getCycleEndDate(cycle.endDate),
                carryoverInvoices: cycle.carryOverInvoices,
                tenant: process.env.REACT_APP_TENANT
            };

            await api.put(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles/${cycle.id}`,
                updatePayload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            await api.put(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles/${cycle.id}/vendors`,
                cycle.vendorIds,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            setOriginalExistingCycles(prev =>
                prev.map(c =>
                    c.id === cycle.id ? structuredClone(cycle) : c
                )
            );

            setEditedExistingCycles(prev => {
                const copy = { ...prev };
                delete copy[String(cycle.id)];
                return copy;
            });

            toast.success('Cycle updated successfully');
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                'Failed to update cycle'
            );
        } finally {
            setIsLoading(false);
        }
    }

    async function saveNewCycle(cycleId: number): Promise<void> {
        try {
            setIsLoading(true);

            const audience = process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            const cycle = newCycles.find(c => c.id === cycleId);

            if (!cycle) {
                setIsLoading(false);
                toast.error('Cycle not found');
                return;
            }

            if (!cycle.startDate || !cycle.endDate) {
                setIsLoading(false);
                toast.error('Start and End dates are required');
                return;
            }

            if (!cycle.vendorIds || cycle.vendorIds.length === 0) {
                setIsLoading(false);
                toast.error('At least one vendor is required');
                return;
            }

            const createPayload = {
                startDate: getCycleStartDate(cycle.startDate),
                endDate: getCycleEndDate(cycle.endDate),
                carryoverInvoices: cycle.carryOverInvoices,
                tenant: process.env.REACT_APP_TENANT
            };

            const createRes = await api.post(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles`,
                createPayload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            const newCycleId = createRes.data?.cycle.cycleId;

            if (!newCycleId) {
                setIsLoading(false);
                throw new Error('Cycle creation did not return an id');
            }

            await api.put(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles/${newCycleId}/vendors`,
                cycle.vendorIds,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            setNewCycles(prev => prev.filter(c => c.id !== cycleId));

            getVendorAndCycleData();

            toast.success('Cycle created successfully');
        } catch (err: any) {  
            setIsLoading(false);

            toast.error(
                err?.response?.data?.message ||
                'Failed to save cycle'
            );
        } finally {
            setIsLoading(false);
        }
    }

    function DateEditCell(props: GridRenderEditCellParams) {
        const { id, field, value } = props;
        const apiRef = useGridApiContext();

        return (
            <DatePicker
                value={value ? dayjs(value) : null}
                onChange={(newValue: Dayjs | null) => {
                    apiRef.current.setEditCellValue({
                        id,
                        field,
                        value: newValue ? newValue.toISOString() : null
                    });
                }}
                slotProps={{
                    textField: {
                        variant: 'standard',
                        fullWidth: true,
                        size: 'small',
                        sx: {
                            mt: 0.1,
                            '& input': {
                                fontSize: 14,
                            }
                        }
                    }
                }}
            />
        );
    }

    const handleAddNewCycle = (): void => {
        setNewCycles(prev => [
            ...prev,
            {
                id: Date.now(),
                startDate: null,
                endDate: null,
                vendorIds: [],
                carryOverInvoices: true
            }
        ]);
    };

    const handleDeleteNewCycle = (id: number): void => {
        setNewCycles(prev => prev.filter(c => c.id !== id));
    };

    const updateCycleField = (
        id: number,
        field: keyof Cycle,
        value: any
    ): void => {
        setNewCycles(prev =>
            prev.map(c =>
                c.id === id ? { ...c, [field]: value } : c
            )
        );
    };

    const cycleTableColumns: GridColDef[] = [
        {
            field: 'id',
            headerName: 'ID',
            width: 80
        },
        {
            field: 'startDate',
            headerName: 'Cycle Start',
            width: 125,
            editable: true,
            renderEditCell: (params) => <DateEditCell {...params} />,
            valueFormatter: (value) =>
                value ? dayjs(value).format('MM/DD/YYYY') : ''
        },
        {
            field: 'endDate',
            headerName: 'Cycle End',
            width: 125,
            editable: true,
            renderEditCell: (params) => <DateEditCell {...params} />,
            valueFormatter: (value) =>
                value ? dayjs(value).format('MM/DD/YYYY') : ''
        },
        {
            field: 'vendorIds',
            headerName: 'Applied Vendors',
            flex: 0.5,
            editable: true,

            valueGetter: (_, row) => row.vendorIds ?? [],

            renderCell: (params) => {
                const ids: string[] = params.value ?? [];

                return eligibleVendors
                .filter(v => ids.includes(v.id))
                .map(v => v.name)
                .join(', ');
            },
            renderEditCell: (params) => {
                const selectedIds: string[] = params.value ?? [];

                return (
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                        <VendorSelector
                            options={eligibleVendors}
                            value={selectedIds}
                            onChange={(selected) => {
                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: 'vendorIds',
                                    value: selected,
                                });
                            }}
                        />
                    </Box>
                );
            },
        },
        {
            field: 'carryOverInvoices',
            headerName: 'Inv. Carry Over',
            width: 150,
            editable: true,
            type: 'singleSelect',
            valueOptions: [
                { value: true, label: 'Yes' },
                { value: false, label: 'No' }
            ],
            valueFormatter: (value) => (value ? 'Yes' : 'No')
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 260,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,

            renderCell: (params) => {
                const id = params.row.id;
                const isDirty = isCycleDirty(id);

                return (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={!isDirty}
                            onClick={() => handleSaveCycleUpdates(id)}
                        >
                            Save
                        </Button>

                        <Button
                            size="small"
                            variant="outlined"
                            disabled={!isDirty}
                            onClick={() => handleClearCycleUpdates(id)}
                        >
                            Clear
                        </Button>

                        <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleDeleteCycle(id)}
                        >
                            Delete
                        </Button>
                    </Box>
                );
            }
        }
    ];

    useEffect(() => {
        if (!isConnected && !isAuthorized) return;

        getVendorAndCycleData();

        const handler = handleCyclesUpdate.current;

        socket.on('vendor', handler);

        return () => {
            socket.off('vendor', handler);
        };
    }, [isConnected]);

    if (!isAuthorized) {
        return (
            <Box sx={{ textAlign: 'center', p: 3 }}>
                <WarningRoundedIcon />
                <p>Not Authorized</p>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box
                sx={{
                    length: '50%',
                    height: 440,
                    mb: 3
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DataGridPro
                        rows={existingCycles}
                        columns={cycleTableColumns}
                        disableRowSelectionOnClick
                        loading={isLoading}
                        hideFooter
                        pageSizeOptions={[10, 25, 50]}
                        processRowUpdate={handleProcessCycleRowUpdate}
                        rowHeight={35}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10,
                                    page: 0
                                }
                            }
                        }}
                    />
                </LocalizationProvider>
            </Box>
            <Card>
                <CardHeader
                    title="Add New Cycles"
                    action={
                        <Button variant="contained" onClick={handleAddNewCycle}>
                            Add Cycle
                        </Button>
                    }
                />
                <CardContent>
                    <Stack spacing={2}>
                        {newCycles.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4, fontSize: 20 }}>
                                No pending cycles to display.  Click "Add Cycle" to add. 
                            </Box>
                        )}
                        {newCycles.map((cycle, index) => {
                            return (
                                <Card key={cycle.id} variant="outlined">
                                    <CardHeader
                                        title={`Cycle ${index + 1}`}
                                        action={
                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    handleDeleteNewCycle(cycle.id)
                                                }
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    />
                                    <CardContent>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            <Box sx={{ flex: .75, minWidth: 250 }}>
                                                <TextField
                                                    type="date"
                                                    label="Cycle Start Date"
                                                    InputLabelProps={{ shrink: true }}
                                                    fullWidth
                                                    value={cycle.startDate || ''}
                                                    onChange={(e) =>
                                                        updateCycleField(
                                                            cycle.id,
                                                            'startDate',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Box>
                                            <Box sx={{ flex: .75, minWidth: 250 }}>
                                                <TextField
                                                    type="date"
                                                    label="Cycle End Date"
                                                    InputLabelProps={{ shrink: true }}
                                                    fullWidth
                                                    value={cycle.endDate || ''}
                                                    onChange={(e) =>
                                                        updateCycleField(
                                                            cycle.id,
                                                            'endDate',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1.5, minWidth: 250 }}>
                                                <VendorSelector
                                                    options={eligibleVendors}
                                                    value={cycle.vendorIds}
                                                    onChange={(ids) =>
                                                        updateCycleField(cycle.id, 'vendorIds', ids)
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 250,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                <Box sx={{ fontSize: 20, pl: 3 }}>
                                                    Carry Over Invoices:
                                                </Box>
                                                <Checkbox
                                                    checked={cycle.carryOverInvoices}
                                                    onChange={(e) =>
                                                        updateCycleField(
                                                            cycle.id,
                                                            'carryOverInvoices',
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            </Box>
                                        </Box>
                                    </CardContent>
                                    <CardActions>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => saveNewCycle(cycle.id)}
                                        >
                                            Save
                                        </Button>
                                    </CardActions>
                                </Card>
                            );
                        })}
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
}

export default CycleManagement;